import { PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Table, notification, Typography } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { httpService } from "../../../../base/services/httpService.service";
import { LocalStorageService } from "../../../../base/services/local-storage.service";
import AuthService from "../../../../base/services/authentication.service";
import { UserRolesEnum } from "../../../auth/userRoles.enum";
import { DeleteComponent } from "../../../admin/workspace/components/DeleteComponent";
import DeletedProjectsModal from "./DeletedProjectsModal";
import { EntityTypeEnum, ModalTypesEnum } from "../../../../utils/enums";
import useRowSelection from "../../../../base/hooks/useRowSelection";
import https from "../../../../base/utils/http";
import DeletedModalButton from "../../../../components/DeletedItemsComponents/DeletedModalButton/DeletedModalButton";
import { withErrorBoundary } from "../../../../components/ErrorBoundary/withErrorBoundary";
import ActionsIcons from "../../../../ui/ActionsIcons/ActionsIcons";
import { DeleteIcon } from "../../../../assets/icons/DeleteIcon";
import { ArrowRightIcon } from "../../../../assets/icons/ArrowRightIcon";
import { ArchiveBoxIcon } from "../../../../assets/icons/ArchiveBoxIcon";
import ArchiveConfirmModal from "../../../../components/Archive/ArchiveConfirmModal";
import ArchivedItemsModal from "../../../../components/Archive/ArchivedItemsModal";
import { archiveWithNotice, isArchiveEnabled } from "../../../../components/Archive/archiveApi";

import "./index.scss";

const { Title } = Typography;

const PAGE_SIZE = 10;

// Who may delete, archive and restore projects.
const PROJECT_MANAGERS = [
   UserRolesEnum.SuperAdmin,
   UserRolesEnum.Organization,
   UserRolesEnum.AdminOfOrganization,
   UserRolesEnum.SeniorProjectManager,
   UserRolesEnum.ProjectManager
];

function ProjectSelection() {
   const [searchValue, setSearchValue] = useState({ searchInput: "", selectedOption: null });
   const [project, setProject] = useState([]);
   const navigate = useNavigate();
   const [deleteModal, setDeleteModal] = useState("");
   const [reload, setReload] = useState(false);
   const [tableLoading, setTableLoading] = useState(false);
   const [pagination, setPagination] = useState({
      page: 1,
      pageSize: PAGE_SIZE,
      totalSize: 0
   });

   const rowClickHandler = (record) => {
      onProjectClick(record);
   };

   const { rowSelection, onRow, selectedRowKeys, setSelectedRowKeys } = useRowSelection(
      rowClickHandler
   );

   const [openDeletedModal, setOpenDeletedModal] = useState(false);
   // Archiving (components/Archive): the project asked about, and the archived list.
   const [archiveTarget, setArchiveTarget] = useState(null);
   const [openArchivedModal, setOpenArchivedModal] = useState(false);
   const canArchive = isArchiveEnabled() && AuthService.isAuthorized(PROJECT_MANAGERS);

   const handleInputChange = (e) => {
      const inputValue = e.target.value;
      setSearchValue({ ...searchValue, searchInput: inputValue });
      if (!inputValue) {
         getProjectsInWorkspace(1, 10, true);
      }
   };

   const handleButtonClick = () => {
      getProjectsInWorkspace(pagination.current, pagination.pageSize);
   };

   const onProjectClick = (record) => {
      LocalStorageService.setItem("currentProjectID", record.key);
      navigate("/dashboard");
   };

   const columns = [
      {
         title: "Project Name",
         dataIndex: "title",
         key: "title",
         render: (text, record) => (
            <div
               className="project-cell"
               style={{ height: "120%" }}
               onClick={() => onProjectClick(record)}
            >
               {text}
            </div>
         )
      },
      {
         title: "Manager",
         dataIndex: "manager",
         key: "manager",
         render: (text, record) => (
            <div className="project-cell" onClick={() => onProjectClick(record)}>
               {text}
            </div>
         )
      },
      {
         title: "Linked Surveys",
         dataIndex: "linkedSurvey",
         key: "linkedSurvey",
         render: (text, record) => (
            <div className="project-cell" onClick={() => onProjectClick(record)}>
               {text}
            </div>
         )
      },
      {
         title: "Members",
         dataIndex: "members",
         key: "members",
         render: (text, record) => (
            <div className="project-cell" onClick={() => onProjectClick(record)}>
               {text}
            </div>
         )
      },
      {
         title: "",
         fixed: "right",
         render: (record) => (
            <ActionsIcons
               actions={[
                  {
                     icon: <DeleteIcon />,
                     tooltip: "Delete",
                     onClick: () => setDeleteModal(record.key),
                     condition: () =>
                        AuthService.isAuthorized([
                           UserRolesEnum.SuperAdmin,
                           UserRolesEnum.Organization,
                           UserRolesEnum.AdminOfOrganization,
                           UserRolesEnum.SeniorProjectManager,
                           UserRolesEnum.ProjectManager
                        ])
                  },
                  {
                     icon: <ArchiveBoxIcon />,
                     tooltip: "Archive",
                     onClick: () => setArchiveTarget(record.key),
                     condition: () => canArchive
                  },
                  {
                     icon: <ArrowRightIcon />,
                     tooltip: "Go to project",
                     onClick: () => onProjectClick(record)
                  }
               ]}
            />
         )
      }
   ];

   const getProjectsInWorkspace = useCallback(
      (bool) => {
         setTableLoading(true);

         const workspcId = LocalStorageService?.getItem("currentWorkspaceId") || "";
         const searchInput = bool ? "" : searchValue.searchInput;

         const params = new URLSearchParams({
            search: searchInput,
            page: pagination.page,
            pageSize: pagination.pageSize,
            isDeleted: false
         });

         httpService.get(
            `/Projects/getMyProjectsInWorkspace/${workspcId}?${params.toString()}`,
            (res) => {
               if (res.isSuccess) {
                  const filteredProjects = res.data.projects.map((project) => ({
                     key: project.id,
                     title: <>{project.name}</>,
                     manager: project.manager.fullName,
                     description: project.description,
                     type: project.name,
                     linkedSurvey: project.countOfSurveys,
                     members: project.projectMembers,
                     importedBeneficiaryTableIds: project.importedBeneficiaryTableIds
                  }));

                  setProject(filteredProjects);

                  setPagination((prev) => ({
                     ...prev,
                     totalSize: res.data?.totalSize || 0,
                     page:
                        res.data?.projects.length === 0 && res.data?.totalSize !== 0
                           ? prev.page > 1
                              ? prev.page - 1
                              : 1
                           : prev.page
                  }));
               }
               setTableLoading(false);
            },
            (err) => {
               notification.error({
                  message: "Oops! A problem has occurred!",
                  description: "We had a problem fetching the projects, please try again."
               });
               setTableLoading(false);
            }
         );
      },
      [pagination.page, pagination.pageSize, searchValue.searchInput]
   );

   useEffect(() => {
      getProjectsInWorkspace();
   }, [getProjectsInWorkspace, reload]);

   const handleDeleteSurvey = async (id) => {
      try {
         const params = new URLSearchParams({
            entityType: EntityTypeEnum.Project,
            isDeleted: true
         });
         const res = await https.put(
            `/DataCollection/${id}/toggleDeleteOrRestore?${params.toString()}`
         );

         notification.open({
            type: res.data.isSuccess ? "success" : "error",
            message: res.data.isSuccess
               ? "Project deleted successfully"
               : "Oops! A problem has occurred!",
            description: res.data.isSuccess ? (
               res?.data?.message || "The project has been deleted."
            ) : (
               <ul>
                  {res?.data?.errors?.map((err) => (
                     <li key={err}>{err}</li>
                  ))}
               </ul>
            )
         });
         if (res.data.isSuccess) {
            setReload((prev) => !prev);
            setDeleteModal("");
         }
      } catch (err) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: (
               <ul>
                  {err.response?.data?.errors?.map((err) => (
                     <li key={err}>{err}</li>
                  ))}
               </ul>
            )
         });
      }
   };

   const handleBulkDelete = async () => {
      try {
         const params = new URLSearchParams({
            entityType: EntityTypeEnum.Project,
            isDeleted: true
         });
         const res = await https.put(
            `/DataCollection/bulkToggleDeleteOrRestore?${params.toString()}`,
            selectedRowKeys
         );

         notification.open({
            type: res.data.isSuccess ? "success" : "error",
            message: res.data.isSuccess
               ? "Projects deleted successfully"
               : "Oops! A problem has occurred!",
            description:
               res.data.message ||
               (res.data.isSuccess
                  ? "The selected projects have been deleted."
                  : "We had a problem deleting the projects, please try again.")
         });
         if (res.data.isSuccess) {
            setReload((prev) => !prev);
            setSelectedRowKeys([]);
         }
      } catch (err) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description:
               err.response?.data?.errors[0] ||
               "We had a problem deleting the projects, please try again."
         });
      }
   };

   // TODO: remove typeId because it doesn't have any use
   const typeId = "31177118-62ad-4650-a8de-fc23644aacf3";

   return (
      <>
         <div className="projects-selection-page table-container">
            <div
               className="container p-0"
               style={{
                  borderBottom: "1px solid #D9E3FF",
                  marginBottom: "20px"
               }}
            >
               <Flex justify="space-between" gap={4} style={{ paddingBottom: "16px" }}>
                  <Flex align="flex-end">
                     <Title
                        level={4}
                        style={{
                           color: "#252938",
                           fontFamily: "Poppins",
                           lineHeight: "24px",
                           letterSpacing: "-0.4px",
                           margin: 0
                        }}
                     >
                        Project List
                     </Title>
                  </Flex>

                  <div>
                     {AuthService.isAuthorized([
                        UserRolesEnum.SuperAdmin,
                        UserRolesEnum.Organization,
                        UserRolesEnum.AdminOfOrganization,
                        UserRolesEnum.SeniorProjectManager,
                        UserRolesEnum.ProjectManager
                     ]) && (
                        <Link to={"/project-selection/create-project/" + typeId}>
                           <Button className="primaryButton" icon={<PlusOutlined />}>
                              Create new project
                           </Button>
                        </Link>
                     )}
                  </div>
               </Flex>
            </div>

            <Flex className="project-selection_table-actions" gap={4}>
               <div className="project-selection_table-actions--filters">
                  <Input.Search
                     placeholder="input search text"
                     size="large"
                     style={{
                        maxWidth: "391px"
                     }}
                     value={searchValue.searchInput}
                     onChange={handleInputChange}
                     onSearch={handleButtonClick}
                  />
               </div>
               <div
                  className={`project-selection_table-actions--extra ${
                     selectedRowKeys.length > 0
                        ? "table-container--delete-selected"
                        : "table-container--delete"
                  }`}
               >
                  <Flex gap={8} justify="flex-end">
                     <DeletedModalButton
                        style={{
                           height: "44px"
                        }}
                        allowedRoles={[
                           UserRolesEnum.SuperAdmin,
                           UserRolesEnum.Organization,
                           UserRolesEnum.AdminOfOrganization,
                           UserRolesEnum.SeniorProjectManager,
                           UserRolesEnum.ProjectManager
                        ]}
                        onClick={() =>
                           selectedRowKeys.length > 0
                              ? setDeleteModal(ModalTypesEnum.BULK)
                              : setOpenDeletedModal(true)
                        }
                     >
                        {selectedRowKeys.length > 0 ? "Delete selected rows" : "Deleted projects"}
                     </DeletedModalButton>
                     {canArchive && (
                        <DeletedModalButton
                           style={{ height: "44px" }}
                           icon={<ArchiveBoxIcon />}
                           onClick={() => setOpenArchivedModal(true)}
                        >
                           Archived projects
                        </DeletedModalButton>
                     )}
                  </Flex>
               </div>
            </Flex>

            <Table
               style={{ overflowX: "auto" }}
               bordered
               prefixCls="table-section"
               dataSource={project}
               columns={columns}
               pagination={{
                  ...pagination,
                  current: pagination.page,
                  pageSize: pagination.pageSize,
                  total: pagination.totalSize,
                  showSizeChanger: true,
                  onChange: (page, pageSize) =>
                     setPagination((prev) => ({ ...prev, page, pageSize }))
               }}
               loading={tableLoading}
               rowSelection={
                  AuthService.isAuthorized([
                     UserRolesEnum.SuperAdmin,
                     UserRolesEnum.Organization,
                     UserRolesEnum.AdminOfOrganization,
                     UserRolesEnum.SeniorProjectManager,
                     UserRolesEnum.ProjectManager
                  ]) && rowSelection
               }
               onRow={onRow}
            />
         </div>

         {deleteModal !== "" && deleteModal !== ModalTypesEnum.BULK && (
            <DeleteComponent
               open={deleteModal.length > 0}
               setOpen={() => setDeleteModal("")}
               showModal={() => setDeleteModal("")}
               handleDelete={handleDeleteSurvey}
               id={deleteModal}
            />
         )}

         {deleteModal === ModalTypesEnum.BULK && selectedRowKeys.length > 0 && (
            <DeleteComponent
               open={deleteModal === ModalTypesEnum.BULK && selectedRowKeys.length > 0}
               setOpen={() => setDeleteModal("")}
               handleDelete={handleBulkDelete}
            />
         )}

         {canArchive && (
            <>
               <ArchiveConfirmModal
                  open={Boolean(archiveTarget)}
                  label="project"
                  onCancel={() => setArchiveTarget(null)}
                  onConfirm={async () => {
                     if (await archiveWithNotice(EntityTypeEnum.Project, archiveTarget)) {
                        setReload((prev) => !prev);
                     }
                     setArchiveTarget(null);
                  }}
               />
               <ArchivedItemsModal
                  entityType={EntityTypeEnum.Project}
                  workspaceId={LocalStorageService.getItem("currentWorkspaceId")}
                  isModalOpen={openArchivedModal}
                  onClose={() => setOpenArchivedModal(false)}
                  onRestored={() => setReload((prev) => !prev)}
               />
            </>
         )}

         <DeletedProjectsModal
            isModalOpen={openDeletedModal}
            setIsModalOpen={setOpenDeletedModal}
            handleOk={() => setReload((prev) => !prev)}
            handleCancel={() => setReload((prev) => !prev)}
         />
      </>
   );
}

export default withErrorBoundary(ProjectSelection);
